import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, User, Wrench } from "lucide-react";
import { SectionHeader, Reveal, Pill } from "@/components/Bits";
import { CASE_STUDIES, PROJECTS } from "@/data/portfolio";

export default function CaseStudy() {
  const { slug } = useParams();
  const study = CASE_STUDIES[slug];
  const meta = PROJECTS.find((p) => p.slug === slug);

  if (!study || !meta) return <Navigate to="/projects" replace />;

  const accent = meta.accent;
  const otherProjects = PROJECTS.filter((p) => p.slug !== slug);

  return (
    <div data-testid={`case-study-${slug}`} className="max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-16">
      {/* Back link */}
      <Link
        to="/projects"
        data-testid="case-back-link"
        className="inline-flex items-center gap-2 text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text)] mb-10"
      >
        <ArrowLeft className="w-4 h-4" /> Back to all projects
      </Link>

      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-8">
          <Pill variant={accent}>Case study</Pill>
          <h1
            data-testid="case-title"
            className="mt-5 font-display text-5xl md:text-7xl font-light leading-[1.02] tracking-tighter"
          >
            {study.title}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[var(--text-muted)] max-w-2xl leading-relaxed">
            {study.subtitle}
          </p>
        </div>

        <div className="lg:col-span-4">
          <div className="soft-card p-6 space-y-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)] flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" /> Role
              </p>
              <p className="mt-1.5 font-semibold">{study.role}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)] flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Duration
              </p>
              <p className="mt-1.5 font-semibold">{study.duration}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)] flex items-center gap-1.5">
                <Wrench className="w-3.5 h-3.5" /> Tools
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {study.tools.map((t) => (
                  <Pill key={t}>{t}</Pill>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cover */}
      <Reveal delay={0.1}>
        <div
          className="mt-14 rounded-3xl overflow-hidden relative h-72 md:h-[420px]"
          style={{ background: `var(--${accent}-soft)` }}
          data-testid="case-cover"
        >
          {study.cover && (
            <img
              src={study.cover}
              alt={study.title}
              className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-90"
            />
          )}
          <div className="absolute inset-0 flex items-end p-8 md:p-12 bg-gradient-to-t from-black/30 to-transparent">
            <span className="font-display text-white text-5xl md:text-7xl opacity-90">{meta.emoji}</span>
          </div>
        </div>
      </Reveal>

      {/* Metrics */}
      <Reveal delay={0.15}>
        <div className="mt-14 soft-card p-2 grid grid-cols-3 divide-x divide-[var(--border-soft)]" data-testid="case-metrics">
          {study.metrics.map((m) => (
            <div key={m.l} className="p-6 md:p-8 text-center">
              <div className="font-display text-3xl md:text-4xl font-light">{m.v}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">{m.l}</div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Sections */}
      <div className="mt-20 grid lg:grid-cols-12 gap-10">
        {/* Sticky TOC */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-28">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">On this page</p>
            <ul className="mt-4 space-y-2">
              {study.sections.map((s) => (
                <li key={s.heading}>
                  <a href={`#${s.heading.toLowerCase()}`} className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text)]">
                    {s.heading}
                  </a>
                </li>
              ))}
              {study.extras?.images?.length > 0 && (
                <li>
                  <a href="#gallery" className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text)]">Gallery</a>
                </li>
              )}
              {study.extras?.nikitaTake && (
                <li>
                  <a href="#my-take" className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text)]">My take</a>
                </li>
              )}
              <li>
                <a href="#learnings" className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text)]">Learnings</a>
              </li>
              {study.extras?.downloads?.length > 0 && (
                <li>
                  <a href="#files" className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text)]">Files</a>
                </li>
              )}
            </ul>
          </div>
        </aside>

        <article className="lg:col-span-9 space-y-14">
          {study.sections.map((s, i) => (
            <Reveal key={s.heading} delay={i * 0.04}>
              <section id={s.heading.toLowerCase()} data-testid={`case-section-${s.heading.toLowerCase()}`}>
                <div className="flex items-center gap-3">
                  <span className="font-display text-[var(--peach)] text-2xl font-medium">0{i + 1}</span>
                  <span className="h-px w-10 bg-[var(--border-soft)]" />
                  <Pill variant={accent}>{s.heading}</Pill>
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-4xl font-light leading-tight">
                  {s.heading}
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-[var(--text-muted)]">{s.body}</p>
              </section>
            </Reveal>
          ))}

          {/* Extras: gallery + Nikita's take + downloads */}
          {study.extras?.images?.length > 0 && (
            <Reveal>
              <section data-testid="case-section-gallery" id="gallery">
                <div className="flex items-center gap-3">
                  <span className="font-display text-[var(--peach)] text-2xl font-medium">+</span>
                  <span className="h-px w-10 bg-[var(--border-soft)]" />
                  <Pill variant={accent}>Visuals from the deck</Pill>
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-4xl font-light leading-tight">
                  Inside the teardown.
                </h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {study.extras.images.map((img) => (
                    <figure key={img.src} className="soft-card overflow-hidden">
                      <div className="bg-[var(--bg-2)] flex items-center justify-center p-4">
                        <img src={img.src} alt={img.caption} className="max-w-full max-h-[520px] w-auto h-auto object-contain rounded-lg" loading="lazy" />
                      </div>
                      <figcaption className="px-5 py-3 text-sm font-medium text-[var(--text-muted)]">{img.caption}</figcaption>
                    </figure>
                  ))}
                </div>
              </section>
            </Reveal>
          )}

          {study.extras?.nikitaTake && (
            <Reveal>
              <section data-testid="case-section-nikita-take" id="my-take" className="soft-card p-8 md:p-10 bg-[var(--peach-soft)]">
                <Pill variant="peach">My take</Pill>
                <h2 className="mt-4 font-display text-3xl md:text-4xl font-light leading-tight">
                  Why this teardown <em className="italic font-medium">stuck with me.</em>
                </h2>
                <p className="mt-5 text-lg leading-relaxed">{study.extras.nikitaTake}</p>
              </section>
            </Reveal>
          )}

          {/* Learnings */}
          <Reveal>
            <section id="learnings" data-testid="case-section-learnings" className="soft-card p-8 md:p-10 bg-[var(--lavender-soft)]">
              <Pill variant="lavender">Learnings</Pill>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-light leading-tight">
                What I&apos;d take with me.
              </h2>
              <p className="mt-5 text-lg leading-relaxed">{study.learnings}</p>
            </section>
          </Reveal>

          {/* Downloads */}
          {study.extras?.downloads?.length > 0 && (
            <Reveal>
              <section data-testid="case-section-downloads" id="files">
                <Pill>Files</Pill>
                <h2 className="mt-4 font-display text-3xl md:text-4xl font-light leading-tight">Grab the full deck.</h2>
                <div className="mt-5 flex flex-wrap gap-3">
                  {study.extras.downloads.map((d) => (
                    <a
                      key={d.href}
                      href={d.href}
                      download
                      data-testid={`case-download-${d.label.replace(/\W+/g, "-").toLowerCase()}`}
                      className="btn-secondary"
                    >
                      <ArrowUpRight className="w-4 h-4" /> {d.label}
                    </a>
                  ))}
                </div>
              </section>
            </Reveal>
          )}

          {/* External CTA */}
          {study.externalLink && !study.extras?.downloads && (
            <Reveal>
              <a
                href={study.externalLink}
                target="_blank"
                rel="noreferrer"
                data-testid="case-external-link"
                className="btn-primary"
              >
                {study.externalLabel || "Open source doc"}
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </Reveal>
          )}
        </article>
      </div>

      {/* Next projects */}
      <div className="mt-32" data-testid="case-other-projects">
        <SectionHeader eyebrow="Keep exploring" title="More" emphasis="case studies." />
        <div className="grid md:grid-cols-2 gap-7">
          {otherProjects.map((p) => (
            <Link
              key={p.slug}
              to={`/projects/${p.slug}`}
              data-testid={`case-next-${p.slug}`}
              className="soft-card group block overflow-hidden"
            >
              <div className="relative h-44 overflow-hidden" style={{ background: `var(--${p.accent}-soft)` }}>
                <div className="absolute inset-0 flex items-center justify-center text-7xl opacity-90">{p.emoji}</div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-medium">{p.title}</h3>
                <p className="mt-2 text-sm text-[var(--text-muted)]">{p.blurb}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold">
                  Read case <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
